import React, { useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { B2BIcon } from 'assets/LeadInfo/B2BIcon';
import { BuildingIcon } from 'assets/LeadInfo/BuildingIcon';
import { UnassignIcon } from 'assets/LeadInfo/UnassignIcon';
import AssignIcon from 'assets/LeadInfo/assign.svg';
import Flag from 'assets/LeadInfo/flag.svg';
import PencilIcon from 'assets/LeadInfo/pencil.svg';
import ReassignIcon from 'assets/LeadInfo/reassign.svg';
import TakeLeadIcon from 'assets/LeadInfo/takeLead.svg';
import Button from 'common-ui/button';
import Loading from 'common-ui/loading';
import CustomSelectComponent from 'common-ui/select/Select';
import { PRIORITY_OPTION } from 'constants/constants';
import { getCustomerAppointmentsByLeadId } from 'features/myAppointments/MyAppointmentsActions';
import {
  bulkChangeLeadStatus,
  getLeadById,
  getWorkflowStatuses,
} from 'features/newsales/newsalesActions';

import AssignLeadModal from '../components/assignLeadModal';
import TakeLeadModal from '../components/takeLeadModal/TakeLeadModal';
import UnassignLeadModal from '../components/unassignLeadModal';
import {
  BackButton,
  BadgeItem,
  BadgeItemStatus,
  CardLeftIcon,
  CardLeftImg,
  CardRightIcon,
  Content,
  ContentBox,
  EditIconButton,
  EmptyMessage,
  ErrorContainer,
  HeaderTop,
  HeaderTopTitle,
  InputWrapper,
  LeadBadge,
  LeadCardLeft,
  LeadCardRight,
  LeadHeaderCard,
  LeadInfoLeft,
  LeadInfoTitle,
  LeadInfoTitleWrapper,
  LeadMetaInfo,
  LeftColumn,
  RightColumn,
} from './LeadInfo.styles';
import CompetitorInfo from './competitorInfo/CompetitorInfo';
import ContactInfo from './contactInfo';
import CustomerAppointments from './customerAppointments';
import DetailInfo from './detailInfo';
import HistoryInfo from './historyInfo';
import OfferInfo from './offerInfo';
import QuickActions from './quickActions';

const LeadInfo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('id');

  const [leadData, setLeadData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workflowStatuses, setWorkflowStatuses] = useState([]);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignModalMode, setAssignModalMode] = useState('assign');
  const [takeModalOpen, setTakeModalOpen] = useState(false);
  const [unassignModalOpen, setUnassignModalOpen] = useState(false);

  const { control } = useForm({
    defaultValues: {
      status: 'pending',
    },
  });

  const fetchLeadData = async () => {
    if (!leadId) {
      setError('No lead ID provided');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await dispatch(getLeadById(leadId)).unwrap();
      setLeadData(response);
    } catch (err) {
      setError(err?.message || 'Failed to fetch lead data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWorkflowStatuses = async () => {
    setIsLoadingStatuses(true);
    try {
      let allStatuses = [];
      let page = 1;
      const limit = 50;
      let hasMore = true;

      while (hasMore) {
        const response = await dispatch(getWorkflowStatuses({ page, limit })).unwrap();
        const statuses = Array.isArray(response) ? response : response?.data || [];
        allStatuses = [...allStatuses, ...statuses];

        // Check if there are more pages
        hasMore = statuses.length === limit;
        page += 1;
      }

      setWorkflowStatuses(allStatuses);
    } catch (err) {
      console.error('Failed to fetch workflow statuses:', err);
    } finally {
      setIsLoadingStatuses(false);
    }
  };

  useEffect(() => {
    if (leadId) {
      dispatch(getCustomerAppointmentsByLeadId(leadId));
    }
  }, [leadId]);

  useEffect(() => {
    fetchLeadData();
    fetchWorkflowStatuses();
  }, [leadId]);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Content>
        <BackButton onClick={() => navigate(-1)}>{'< Back'}</BackButton>
        <HeaderTop>
          <HeaderTopTitle>Lead Info</HeaderTopTitle>
          <Button secondary className="add-button" onClick={() => navigate('/sales-new')}>
            + Add Lead
          </Button>
        </HeaderTop>
        <ErrorContainer>Error: {error}</ErrorContainer>
      </Content>
    );
  }

  if (!leadData) {
    return (
      <Content>
        <BackButton onClick={() => navigate(-1)}>{'< Back'}</BackButton>
        <HeaderTop>
          <HeaderTopTitle>Lead Info</HeaderTopTitle>
          <Button secondary className="add-button" onClick={() => navigate('/sales-new')}>
            + Add Lead
          </Button>
        </HeaderTop>
        <EmptyMessage>No lead data found</EmptyMessage>
      </Content>
    );
  }

  const isB2B = leadData.businessType === 'B2B';
  const fullName = [leadData.firstName, leadData.patronymic, leadData.lastName]
    .filter(Boolean)
    .join(' ');
  const displayName = isB2B
    ? leadData.companyName || fullName || 'Unnamed Lead'
    : fullName || leadData.companyName || 'Unnamed Lead';

  // Calculate total orders across all offers
  const totalOrders =
    leadData.offers?.reduce((total, offer) => total + (offer.orders?.length || 0), 0) || 0;

  const workflowStatusOptions = workflowStatuses
    .filter((status) => status.id !== leadData?.workflowStatus?.id)
    .map((status) => ({
      value: status.id,
      label: status.name,
    }));

  const handleStatusChange = async (option) => {
    if (!option || !leadData?.id) return;

    try {
      await dispatch(
        bulkChangeLeadStatus({
          leadIds: [leadData.id],
          workflowStatusId: option.value,
        })
      ).unwrap();

      await fetchLeadData();
    } catch (error) {
      console.error('Failed to update lead status:', error);
    }
  };

  const handleOpenAssignModal = (mode) => {
    setAssignModalMode(mode);
    setAssignModalOpen(true);
  };

  const handleAssignSuccess = () => {
    fetchLeadData();
  };

  const handleEdit = () => {
    if (leadData?.id) {
      navigate(`/sales-new?editMode=true&leadId=${leadData.id}`);
    }
  };

  return (
    <Content>
      <BackButton onClick={() => navigate(-1)}>{'< Back'}</BackButton>
      <HeaderTop>
        <HeaderTopTitle>Lead Info</HeaderTopTitle>
        <Button secondary className="add-button" onClick={() => navigate('/sales-new')}>
          + Add Lead
        </Button>
      </HeaderTop>
      <LeadHeaderCard>
        <LeadCardLeft>
          <CardLeftImg>{isB2B ? <B2BIcon /> : <BuildingIcon />}</CardLeftImg>
          <LeadInfoLeft>
            <LeadInfoTitleWrapper>
              <LeadInfoTitle>{displayName}</LeadInfoTitle>
              <EditIconButton onClick={handleEdit}>
                <img src={PencilIcon} alt="Edit" />
              </EditIconButton>
            </LeadInfoTitleWrapper>
            <LeadBadge>
              {leadData.workflowStatus?.name && (
                <BadgeItem>{leadData.workflowStatus.name}</BadgeItem>
              )}
              {leadData.priority &&
                (() => {
                  const priority = PRIORITY_OPTION.find(
                    (elm) => elm.id === leadData.priority.toLowerCase()
                  );
                  return (
                    <BadgeItem
                      className="priority"
                      style={
                        priority
                          ? {
                              background: priority.bg,
                              color: priority.text,
                            }
                          : {}
                      }
                    >
                      <CardLeftIcon src={Flag} /> {leadData.priority}
                    </BadgeItem>
                  );
                })()}
            </LeadBadge>
            <LeadMetaInfo>
              Lead ID: {leadData.leadNumber} • Created:{' '}
              {new Date(leadData.createdAt).toLocaleDateString()}
              {leadData.createdBy && (
                <>
                  {' '}
                  • Created By:{' '}
                  {leadData.createdBy.name && leadData.createdBy.surname
                    ? `${leadData.createdBy.name} ${leadData.createdBy.surname}`
                    : leadData.createdBy.name || leadData.createdBy.email || 'N/A'}
                </>
              )}{' '}
              • Orders: {totalOrders} • Last Activity:{' '}
              {new Date(leadData.updatedAt).toLocaleDateString()}
            </LeadMetaInfo>
          </LeadInfoLeft>
        </LeadCardLeft>
        <LeadCardRight>
          <div className="badge-div">
            {!leadData.assigneeUuid && (
              <BadgeItemStatus onClick={() => handleOpenAssignModal('assign')}>
                <CardRightIcon src={AssignIcon} />
                Assign
              </BadgeItemStatus>
            )}
            {leadData.assigneeUuid && (
              <>
                <BadgeItemStatus
                  className="reassign"
                  onClick={() => handleOpenAssignModal('reassign')}
                >
                  <CardRightIcon src={ReassignIcon} />
                  Reassign
                </BadgeItemStatus>
                <BadgeItemStatus
                  className="unassign"
                  onClick={() => setUnassignModalOpen(true)}
                >
                  <UnassignIcon />
                  Unassign
                </BadgeItemStatus>
              </>
            )}
            {!leadData.assigneeUuid && (
              <BadgeItemStatus className="take-lead" onClick={() => setTakeModalOpen(true)}>
                <CardRightIcon src={TakeLeadIcon} />
                Take Lead
              </BadgeItemStatus>
            )}
          </div>
          <InputWrapper>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <CustomSelectComponent
                  req={true}
                  options={workflowStatusOptions}
                  isSearchable={false}
                  value={null}
                  onChange={(option) => {
                    field.onChange(option);
                    handleStatusChange(option);
                  }}
                  placeholder="Status Update"
                  blurInputOnSelect={true}
                  isLoading={isLoadingStatuses}
                  maxMenuHeight={200}
                />
              )}
            />
          </InputWrapper>
        </LeadCardRight>
      </LeadHeaderCard>
      <ContentBox>
        <LeftColumn>
          <ContactInfo leadData={leadData} onUpdate={fetchLeadData} />
          <DetailInfo leadData={leadData} />
          <CompetitorInfo leadData={leadData} />
          <OfferInfo leadData={leadData} onUpdate={fetchLeadData} />
          <HistoryInfo leadData={leadData} />
        </LeftColumn>
        <RightColumn>
          <QuickActions leadData={leadData} />
          <CustomerAppointments />
        </RightColumn>
      </ContentBox>

      <AssignLeadModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        leadData={leadData?.id ? [leadData.id] : []}
        onSuccess={handleAssignSuccess}
        mode={assignModalMode}
      />
      <TakeLeadModal
        leadIds={leadData?.id ? [leadData.id] : []}
        isOpen={takeModalOpen}
        onSuccess={handleAssignSuccess}
        onClose={() => setTakeModalOpen(false)}
      />
      <UnassignLeadModal
        leadIds={leadData?.id ? [leadData.id] : []}
        isOpen={unassignModalOpen}
        onSuccess={handleAssignSuccess}
        onClose={() => setUnassignModalOpen(false)}
      />
    </Content>
  );
};

export default LeadInfo;
